import { type ReactNode, useEffect, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft, IconInfoCircle } from '@tabler/icons-react';
import { useMachine } from '@xstate/react';
import { useObservability } from 'helpers/observability';
import { useRouter } from 'next/router';
import {
  isStepComplete,
  offerPortalWizardMachine,
  WIZARD_STEP_ORDER,
  WizardStep,
} from 'state_machines/OfferPortalWizardMachine';

import DataForDocumentsStep from './DataForDocumentsStep';
import DetailsStep from './DetailsStep';
import DocumentsStep from './DocumentsStep';
import ExitOfferDialog from './ExitOfferDialog';
import { type ListingDetails } from './listingDetails';
import {
  confirmationFromSubmission,
  readOfferConfirmation,
  storeOfferConfirmation,
} from './offerConfirmationHandoff';
import { clearOfferDraft, readOfferDraft } from './offerDraft';
import OfferTermsStep from './OfferTermsStep';
import ReviewStep from './ReviewStep';
import SubmittingAsStep from './SubmittingAsStep';
import { SummaryCard } from './SummaryCard';
import { type SoftExit, useOfferExitGuard } from './useOfferExitGuard';
import { useOfferPortalAnalytics } from './useOfferPortalAnalytics';
import {
  WIZARD_COLUMNS,
  WIZARD_FORM_COL,
  WIZARD_PAGE_GUTTER,
  WIZARD_PAGE_WIDTH,
  WizardHeader,
  WizardProgress,
} from './wizardFields';

/** Reassurance under the submit action, per design. */
const SUBMIT_NOTE = 'No commitment yet. You’ll review before signing.';

const STEP_TITLES: Record<WizardStep, string> = {
  [WizardStep.OfferTerms]: 'Offer terms',
  [WizardStep.Details]: 'Details',
  [WizardStep.DataForDocuments]: 'Data for documents',
  [WizardStep.SubmittingAs]: 'Submitting as',
  [WizardStep.Documents]: 'Documents',
  [WizardStep.Review]: 'Review',
  // The submission phases render as the review step.
  [WizardStep.Submitting]: 'Review',
  [WizardStep.Submitted]: 'Review',
};

/**
 * Names each step in journey events. Every step of the wizard lives on one
 * route, so there is no page view to separate them; without a per-step name
 * the funnel cannot show where an agent stopped.
 */
const STEP_ANALYTICS_SLUG: Record<WizardStep, string> = {
  [WizardStep.OfferTerms]: 'offer-terms',
  [WizardStep.Details]: 'details',
  [WizardStep.DataForDocuments]: 'data-for-documents',
  [WizardStep.SubmittingAs]: 'submitting-as',
  [WizardStep.Documents]: 'documents',
  [WizardStep.Review]: 'review',
  [WizardStep.Submitting]: 'submitting',
  [WizardStep.Submitted]: 'submitted',
};

/** The submission phases sit on the review step for display purposes. */
const displayStep = (step: WizardStep): WizardStep =>
  step === WizardStep.Submitting || step === WizardStep.Submitted ? WizardStep.Review : step;

const submitLabel = (step: WizardStep): string => {
  if (step === WizardStep.Submitting) return 'Submitting…';
  return step === WizardStep.Submitted ? 'Open confirmation' : 'Submit an offer';
};

const WizardShell = ({
  step,
  listing,
  offerCents,
  onBack,
  onContinue,
  continueLabel = 'Continue',
  continueDisabled = false,
  note,
  children,
}: {
  step: WizardStep;
  listing: ListingDetails;
  offerCents: number | null;
  onBack: () => void;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  note?: string;
  children: ReactNode;
}) => (
  <Box as="section" className="w-full flex-col items-center bg-od-bg-primary pb-28">
    <WizardProgress
      step={WIZARD_STEP_ORDER.indexOf(displayStep(step)) + 1}
      of={WIZARD_STEP_ORDER.length}
    />
    {/* The gutter sits outside the max-width so the two columns keep their
        full 784px; padding on the capped element eats into the content. */}
    <Box className={WIZARD_PAGE_GUTTER}>
      <Col className={WIZARD_PAGE_WIDTH}>
        <Box className="w-full max-w-[493px]">
          <WizardHeader
            step={WIZARD_STEP_ORDER.indexOf(displayStep(step)) + 1}
            of={WIZARD_STEP_ORDER.length}
            title={STEP_TITLES[step]}
            back={
              <Button
                type="button"
                variant="secondary"
                onClick={onBack}
                className="size-9 rounded-[10px] p-0"
                aria-label="Back"
                analyticsName={`cosmos-make-offer-back-${STEP_ANALYTICS_SLUG[displayStep(step)]}`}
              >
                <IconChevronLeft className="size-5" />
              </Button>
            }
          />
        </Box>
        <Row className={WIZARD_COLUMNS}>
          {/* min-w-0 so a long uploaded file name cannot widen the column. */}
          <Col className={WIZARD_FORM_COL}>{children}</Col>
          <SummaryCard
            listing={listing}
            offerCents={offerCents}
            continueLabel={continueLabel}
            onContinue={onContinue}
            continueDisabled={continueDisabled}
            note={note}
            analyticsName={`cosmos-make-offer-continue-${STEP_ANALYTICS_SLUG[displayStep(step)]}`}
          />
        </Row>
      </Col>
    </Box>
  </Box>
);

/**
 * The offer wizard, steps 1–6 per design, driven by the wizard state machine
 * (see `state_machines/OfferPortalWizardMachine.ts`): the machine owns the
 * answers, validation, step flow, and the submission itself; the step
 * components render its context. Continue persists the draft to
 * sessionStorage so a refresh mid-flow keeps the agent's answers.
 */
const OfferWizard = ({ listing, slug }: { listing: ListingDetails; slug: string }) => {
  const router = useRouter();
  const [state, send] = useMachine(offerPortalWizardMachine, { input: { slug, listing } });
  const step = state.value as WizardStep;
  const { context } = state;
  const [navError, setNavError] = useState(false);
  const [pendingExit, setPendingExit] = useState<SoftExit | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);
  const askToLeave = (exit: SoftExit) => {
    setExitError(null);
    setPendingExit(exit);
  };
  useOfferExitGuard({
    enabled: step !== WizardStep.Submitted && !leaving,
    onSoftExit: askToLeave,
  });
  const { trackJourneyEvent } = useObservability();
  const reportedSteps = useRef(new Set<WizardStep>());
  const { trackSubmitAttempt } = useOfferPortalAnalytics(context, step);

  // Restore the draft after mount: sessionStorage is client-only, so reading
  // it during the initial render diverges from the server HTML and trips
  // React hydration.
  useEffect(() => {
    const draft = readOfferDraft(slug);
    if (draft) {
      send({ type: 'HYDRATE', draft });
    }
  }, [slug, send]);

  // Each step starts at its heading rather than wherever the previous step's
  // Continue button left the scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // A created offer hands off to the confirmation page. The draft survives
  // until the page actually opens, so a failed transition leaves the review
  // intact; the machine's Submitted state is terminal, so a retry can only
  // be this navigation — never another POST.
  const openConfirmation = async (): Promise<void> => {
    // A confirmation left by an earlier offer in this tab is still unexpired,
    // so the handoff only counts when it is this offer's.
    const handedOff = (): boolean =>
      context.offerId != null && readOfferConfirmation()?.offerId === context.offerId;

    if (context.offerId != null && !handedOff()) {
      storeOfferConfirmation(
        confirmationFromSubmission(slug, context.offerId, context.priceCents ?? 0, listing),
      );
    }
    if (!handedOff()) {
      // Storage unavailable: the confirmation email the backend sends still
      // arrives; the recovery copy below explains that.
      setNavError(true);
      return;
    }
    try {
      if (await router.push('/make-offer/confirm')) {
        clearOfferDraft();
        return;
      }
    } catch {
      // fall through to the recovery message
    }
    setNavError(true);
  };

  useEffect(() => {
    if (step === WizardStep.Submitted) {
      void openConfirmation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per Submitted entry
  }, [step]);

  // Reported here rather than by an element impression because a step is
  // reached, not seen: an impression re-fires whenever the agent walks back
  // through a step, which reads as step N outnumbering step N-1, a shape a
  // drop-off funnel cannot interpret. One report per step per visit answers
  // "how far did they get".
  const shownStep = displayStep(step);
  useEffect(() => {
    if (reportedSteps.current.has(shownStep)) {
      return;
    }
    reportedSteps.current.add(shownStep);
    try {
      trackJourneyEvent('platform', 'boxViewEvent', {
        targetName: `cosmos-make-offer-step-${STEP_ANALYTICS_SLUG[shownStep]}`,
      });
    } catch {
      // Measuring the funnel must never be able to break the offer being in it.
    }
  }, [shownStep, trackJourneyEvent]);

  const stayInOffer = () => {
    if (!leaving) setPendingExit(null);
  };
  const leaveOffer = async () => {
    if (pendingExit == null) return;
    setLeaving(true);
    setExitError(null);
    try {
      if (await pendingExit.follow()) {
        setPendingExit(null);
        return;
      }
    } catch {
      // A failed navigation must keep the offer and its exit protection active.
    }
    setLeaving(false);
    setExitError('We couldn’t open that page. Try again or stay in your offer.');
  };
  const exitDialog = (
    <ExitOfferDialog
      open={pendingExit != null}
      leaving={leaving}
      error={exitError}
      onStay={stayInOffer}
      onLeave={leaveOffer}
    />
  );
  if (step === WizardStep.OfferTerms) {
    return (
      <>
        <OfferTermsStep listing={listing} context={context} send={send} />
        {exitDialog}
      </>
    );
  }

  const handleBack = () => {
    send({ type: 'BACK' });
  };

  const handleContinue = () => {
    if (step === WizardStep.Submitting) {
      return;
    }
    if (step === WizardStep.Submitted) {
      setNavError(false);
      void openConfirmation();
      return;
    }
    if (step === WizardStep.Review) {
      trackSubmitAttempt();
      send({ type: 'SUBMIT' });
      return;
    }
    send({ type: 'CONTINUE' });
  };

  const reviewError = () => {
    if (navError) {
      return 'Your offer was received, but the confirmation page did not open. Use the button to try again. The confirmation also arrives by email.';
    }
    return context.submitError;
  };

  const onReview =
    step === WizardStep.Review || step === WizardStep.Submitting || step === WizardStep.Submitted;

  return (
    <>
      <WizardShell
        step={step}
        listing={listing}
        offerCents={context.priceCents}
        onBack={handleBack}
        continueLabel={onReview ? submitLabel(step) : 'Continue'}
        // Submitting is in flight, and a step with gaps cannot advance; the
        // review step is always complete by construction.
        continueDisabled={step === WizardStep.Submitting || !isStepComplete(step, context)}
        note={step === WizardStep.Review ? SUBMIT_NOTE : undefined}
        onContinue={handleContinue}
      >
        {onReview && reviewError() != null ? (
          <Row
            role="alert"
            className="items-start gap-3 rounded-[12px] bg-od-bg-error-secondary p-4"
          >
            <IconInfoCircle className="size-5 shrink-0 text-od-content-error" />
            <Text className="text-body-sm text-od-content-error">{reviewError()}</Text>
          </Row>
        ) : null}
        {step === WizardStep.Details ? (
          <DetailsStep listing={listing} context={context} send={send} />
        ) : null}
        {step === WizardStep.DataForDocuments ? (
          <DataForDocumentsStep context={context} send={send} />
        ) : null}
        {step === WizardStep.SubmittingAs ? (
          <SubmittingAsStep listing={listing} context={context} send={send} />
        ) : null}
        {step === WizardStep.Documents ? (
          <DocumentsStep listing={listing} context={context} send={send} />
        ) : null}
        {onReview ? <ReviewStep context={context} send={send} /> : null}
      </WizardShell>
      {exitDialog}
    </>
  );
};

export default OfferWizard;
